<template>
  <admin-wrapper
    ref="water-balance-settings-admin-wrapper"
    :has-buttons="inputs.length ? true : false"
    :inputs="inputs"
    :is-form-fetched="isFetched"
    :is-custom-saving="saving"
    :straight-title="additionalModuleName"
    :moduleEnabler="moduleEnabler"
    @save="save"
  >
    <template #[`balanceProfile.input`]="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="balanceProfile" />
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'WaterBalanceSettings',
  components: {
    AdminWrapper,
    DataInput,
  },
  data: () => ({
    balanceProfile: null,
    isFormFetched: false,
    isFetched: false,
    saving: false,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules@WATER_BALANCE_MODULE.verbose_name'),

    defaultBalanceProfile: get('waterBalance/defaultBalanceProfile'),
    waterBalanceModuleEnabled: get('admin/settingsValues@water_balance_module_enabled'),
    waterBalanceBalanceEnabled: get('admin/settingsValues@water_balance_balance_enabled'),
    waterBalanceIwaEnabled: get('admin/settingsValues@water_balance_iwa_enabled'),
    waterBalanceDmaEnabled: get('admin/settingsValues@water_balance_dma_enabled'),

    balanceProfiles: get('waterBalance/balanceProfiles'),
    iwaProfiles: get('waterBalance/iwaProfiles'),
    dmaProfiles: get('waterBalance/dmaProfiles'),

    inputs() {
      if (!this.waterBalanceModuleEnabled) {
        return [];
      }
      const inputs = [];
      if (this.waterBalanceBalanceEnabled) {
        inputs.push({
          name: 'balanceProfile',
          translationPath: 'admin.waterBalanceDefaultProfile',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.balanceProfiles,
          itemText: 'name',
          itemValue: 'id',
          isCustom: true,
          disabled: this.saving,
        });
      }
      if (this.waterBalanceIwaEnabled) {
        inputs.push(
          ...[
            {
              name: 'water_balance_iwa_monthly_profile',
              translationPath: 'admin.waterBalanceIwaMonthlyDefaultProfile',
              rules: [v => !!v || v == '0' || 'rules.required'],
              dataType: {
                name: 'select',
              },
              align: 'start',
              items: this.iwaProfiles.filter(profile => profile.iwa_type === 'monthly'),
              itemText: 'name',
              itemValue: 'id',
              disabled: this.saving,
            },
            {
              name: 'water_balance_iwa_annual_profile',
              translationPath: 'admin.waterBalanceIwaYearlyDefaultProfile',
              rules: [v => !!v || v == '0' || 'rules.required'],
              dataType: {
                name: 'select',
              },
              align: 'start',
              items: this.iwaProfiles.filter(profile => profile.iwa_type === 'annual'),
              itemText: 'name',
              itemValue: 'id',
              disabled: this.saving,
            },
          ]
        );
      }
      if (this.waterBalanceDmaEnabled) {
        inputs.push({
          name: 'water_balance_dma_generator_profile',
          translationPath: 'admin.waterBalanceDmaDefaultProfile',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.dmaProfiles,
          itemText: 'name',
          itemValue: 'id',
          disabled: this.saving,
        });
      }
      return inputs;
    },
    moduleEnabler() {
      return {
        name: 'water_balance_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
  },
  methods: {
    getBalanceProfiles: call('waterBalance/getBalanceProfiles'),
    getIwaProfiles: call('waterBalance/getIwaProfiles'),
    getDmaProfiles: call('waterBalance/getDmaProfiles'),

    setDefaultBalanceProfile: call('waterBalance/setDefaultBalanceProfile'),

    async init() {
      try {
        await Promise.all([this.getBalanceProfiles(), this.getIwaProfiles(), this.getDmaProfiles()]);
        this.inputs.forEach(input => {
          this[input.name] = this[`default${this.$_capitalize(input.name)}`] || null;
        });
        this.isFetched = true;
        this.$nextTick(() => {
          this.$refs['water-balance-settings-admin-wrapper']?.$refs.form?.$refs.form?.resetValidation();
        });
      } catch {
        //
      }
    },
    async save() {
      try {
        this.saving = true;
        const promises = this.inputs.map(input => {
          const inputName = input.name;
          return this[inputName] === this[`default${this.$_capitalize(inputName)}`]
            ? undefined
            : this[`setDefault${this.$_capitalize(inputName)}`](this[inputName]);
        });
        const filterPromises = promises.filter(promise => !!promise);
        await Promise.all(filterPromises);
        if (filterPromises.length) {
          await this.init();
        }
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
        this.saving = false;
      } catch {
        this.saving = false;
      }
    },
  },
  created() {
    if (this.waterBalanceModuleEnabled) {
      this.init();
    } else {
      this.isFetched = true;
    }
  },
};
</script>
